/* eslint-disable complexity */
import React, {useState, useEffect} from "react";
import PropTypes from "prop-types";
import loadable from "@loadable/component";
import styles from "./styles.css";
import disableScroll from "../../../utils/helpers/disable-scroll";
import { PICKUP_STATUS, POST_PICKUP_SCHEDULED_STATES, PRICE_DISCOVERY } from "../../../constants/seller-diy-constants";
import { getDocumentList } from "../post-inspection-diy-pickup-things-to-do/selector";
import IconSpinner from "../icon-spinner";
import { TAB_NAMES } from "../post-inspection-diy-header-v2/constant";

const PostInspectionDiyPickupScheduling = loadable(() => import("../post-inspection-diy-pickup-scheduling"));
const PostInspectionDiyPickupScheduled = loadable(() => import("../post-inspection-diy-pickup-scheduled"));

// eslint-disable-next-line max-statements
const PostInspectionDiySchedulePickup = ({
    inspectionResponse,
    showDocumentList,
    isStandAlone = true,
    doctet,
    getPickupSlotsConnect,
    setActiveTabConnect,
    activeTab,
    pickupDetail
}) => {
    const { pickupDocuments = {} } = doctet || {};
    const data = getDocumentList(pickupDocuments, isStandAlone);
    const {
        isMandatoryDocRejected,
        isNonMandatoryDocRejected,
        areAllDocumentsApproved
    } = data || {};
    const { token, pickupStatus, isDeliveryApproved = false, priceDiscovery } = inspectionResponse || {};
    const isPriceAccepted = priceDiscovery === PRICE_DISCOVERY.ACCEPTED;
    const { slotExpired } = pickupDetail || {};
    const [isLoading, setIsLoading] = useState(false);
    const isPickupScheduled = pickupStatus === PICKUP_STATUS.PICKUP_SCHEDULED;
    const isPickupNotScheduled = pickupStatus === PICKUP_STATUS.PICKUP_NOT_SCHEDULED;
    const isPickUpInProgress = pickupStatus === PICKUP_STATUS.PICKUP_IN_PROGRESS;
    const isDriverAssigned = pickupStatus === PICKUP_STATUS.DRIVER_ASSIGNED;
    const [showPickUpSchedule, setShowPickupSchedule] = useState(!isDeliveryApproved);

    useEffect(() => {
        if (POST_PICKUP_SCHEDULED_STATES.includes(pickupStatus)  && !showPickUpSchedule && isDeliveryApproved)   setActiveTabConnect(TAB_NAMES.PICKUP_PAYMENT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        disableScroll(showDocumentList, "hidden");
    }, [showDocumentList]);

    useEffect(() => {
        if (activeTab === TAB_NAMES.PICKUP_PAYMENT) setShowPickupSchedule(false);
    }, [activeTab]);

    const onClickHandler = () => {
        setIsLoading(true);
        setActiveTabConnect(TAB_NAMES.PICKUP);
        getPickupSlotsConnect(token).then(() => {
            setIsLoading(false);
            setShowPickupSchedule(true);
        }).catch(() => {
            setIsLoading(false);
        });
    };

    const getTitle = () => {
        if (isDeliveryApproved && isPickupNotScheduled) return "Schedule a pickup for your car";
        if (isPickUpInProgress) return "Pick-up in progress!";
        if (slotExpired) return "Pick-up cannot be done as per schedule";
        if (isPickupScheduled || isDriverAssigned) return "Your car pickup details";
        return "Schedule Pick up Date & Time";
    };

    const getDocSubtitle = () => {
        if (!areAllDocumentsApproved) return "Some documents have issues! Please share right set of documents.";
        return "Please help the driver with the right set of documents";
    };

    const getSubTitle = () => {
        if (isPickUpInProgress) return getDocSubtitle();
        if (isPickupNotScheduled) return "";
        if (isDeliveryApproved) return "Driver has been assigned for your car pickup";
        if (isPickupNotScheduled || slotExpired) return "";
        return null;
    };

    useEffect(() => {
        if (!isDeliveryApproved && activeTab !== TAB_NAMES.PICKUP_PAYMENT) {
            setIsLoading(true);
            getPickupSlotsConnect(token).then(() => {
                setIsLoading(false);
            }).catch(() => {
                setIsLoading(false);
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const renderCta = () => {
        if (activeTab === TAB_NAMES.PICKUP_PAYMENT) {
            return (
                <PostInspectionDiyPickupScheduled onClick={onClickHandler}/>
            );
        }
        return (<React.Fragment>
            {showPickUpSchedule && !isLoading &&
                <div styleName={"styles.schedulePickup"}>
                    <PostInspectionDiyPickupScheduling
                        onClose={() => {setShowPickupSchedule(false);}}
                        isRescheduling={isPickupScheduled}
                    />
                </div>
            }
        </React.Fragment>
        );
    };

    const getStatus = () => {
        if (isPickupNotScheduled) return "";
        if (areAllDocumentsApproved) return "VERIFICATION SUCCESSFUL";
        if (isMandatoryDocRejected && isNonMandatoryDocRejected) return "VERIFICATION FAILED";
        if (isMandatoryDocRejected) return "PARTIALLY VERIFIED";
        return "";
    };

    const getStyles = () => {
        if (isPickupNotScheduled) return "";
        if (areAllDocumentsApproved) return  "styles.verified";
        if (isMandatoryDocRejected && isNonMandatoryDocRejected) return "styles.failed";
        if (isMandatoryDocRejected) return "styles.verified";
        return "";
    };

    const renderHeading = () => {
        if (activeTab === TAB_NAMES.PICKUP) {
            return (
                <div styleName={"styles.pickUpWrapper"}>
                    <h3>Schedule Pick up Date & Time</h3>
                </div>
            );
        }
        if (POST_PICKUP_SCHEDULED_STATES.includes(pickupStatus) && !showPickUpSchedule) {
            return (
                <div styleName={"styles.pickUpWrapper"}>
                    <h3>{getTitle()}</h3>
                    <p>{getSubTitle()}</p>
                    {isPickUpInProgress &&
                        <p styleName={getStyles()}>
                            {getStatus()}
                        </p>
                    }
                </div>
            );
        }
        return "";
    };
    if (isLoading) return <div styleName={"styles.loadingIcon"}><IconSpinner /></div>;
    return (
        <div styleName={"styles.schedulePickupWrapper"}>
            {isPriceAccepted && isDeliveryApproved && <div styleName={"styles.schedulePickupHeader"}>
                {renderHeading()}
            </div>}
            {renderCta()}
        </div>
    );
};

PostInspectionDiySchedulePickup.propTypes = {
    inspectionResponse: PropTypes.object,
    showDocumentList: PropTypes.bool,
    getPickupSlotsConnect: PropTypes.func,
    doctet: PropTypes.object,
    isStandAlone: PropTypes.bool,
    setActiveTabConnect: PropTypes.func,
    activeTab: PropTypes.any,
    pickupDetail: PropTypes.object
};

export default PostInspectionDiySchedulePickup;
